
import { initializeApp, getApps } from "firebase/app";
import {
	ref,
	getDatabase,
	get as nativeGet,
	push as nativePush,
	update as nativeUpdate,
	remove as nativeRemove,
	onValue as nativeOnValue,
	onChildAdded as nativeOnChildAdded,
	off as nativeOff,
	set as nativeSet,
	serverTimestamp,
} from "firebase/database";
import {
	browserSessionPersistence,
	getAuth,
	setPersistence,
	signInWithCustomToken,
} from "firebase/auth";
import bdjs from "./bdjs";

const fb = () => {
	const getRefPath = (path) => ref(getDatabase(), path);
	const getTeamPath = (team_code) => window.CFG.firebase.environment + '/' + team_code.toUpperCase();
	const getPlayerPath = (team_code, player_id) => getTeamPath(team_code) + '/players/' + player_id;
	const getRefTeam = (team_code) => getRefPath(getTeamPath(team_code));
	const getRefPlayer = (team_code, player_id) => getRefPath(getPlayerPath(team_code, player_id));

	const init = async (auth_token) => {
		if (getApps().length === 0) {
			const config = {
				apiKey: window.CFG.firebase.key,
				authDomain: window.CFG.firebase.auth_domain,
				databaseURL: window.CFG.firebase.database_url,
			};

			initializeApp(config);
		}
		/**
		 * NOTE: IMPORTANT
		 * Avoid using promise.catch() for every firebase auth, you should always use
		 * try catch block instead.
		 * see:
		 * https://github.com/firebase/firebase-js-sdk/issues/4242
		 * https://github.com/firebase/firebase-js-sdk/issues/1881
		 */
		try {
			const auth = getAuth();
			const loginFb = await signInWithCustomToken(
				auth,
				auth_token,
			);
			setPersistence(auth, browserSessionPersistence)
				.then(() => {
					// New sign-in will be persisted with session persistence.
					loginFb;
				})
				.catch((error) => {
					// Handle Errors here.
					console.error(error);
				});
		} catch (err) {
			// // in case error happen, Get new token and retry to initFirebase again
			// const newToken = await getNewFirebaseToken();
			// CONFIG.firebase.auth_token = newToken;
			// return await init();
			console.error(err);
			return false;
		}

		return true;
	}


	const firebaseListener = async (team_code) => {
		nativeOnValue(
			getRefTeam(team_code),
			(snapshot) => {
				const value = snapshot.val();
				bdjs.trigger("bd__teamAttributeChanged", value);
			},
			{ onlyOnce: false },
		);

		// Listen for new answered questions
		const answeredQuestionsRef = getRefPath(getTeamPath(team_code) + '/answered_questions');

		// First, get the current data to determine what already exists
		const currentSnapshot = await nativeGet(answeredQuestionsRef);
		const existingCount = currentSnapshot.exists() ? Object.keys(currentSnapshot.val()).length : 0;
		let loadedCount = 0;

		nativeOnChildAdded(
			answeredQuestionsRef,
			(snapshot) => {
				// Skip existing items during initial load
				loadedCount++;
				if (loadedCount <= existingCount) {
					return;
				}

				const value = snapshot.val();

				// Handle both old format (just answer_id) and new format (object with answer_id and answered_at)
				let answeredQuestion;
				if (typeof value === 'object' && value !== null) {
					// New format: {answer_id: X, answered_at: Y}
					answeredQuestion = {
						question_id: snapshot.key,
						answer_id: value.answer_id,
						answered_at: value.answered_at
					};
				} else {
					// Old format: just the answer_id value
					answeredQuestion = {
						question_id: snapshot.key,
						answer_id: value
					};
				}

				bdjs.trigger("bd__answeredQuestionAdded", answeredQuestion);
			}
		);

		// Listen for new workaround results
		const workaroundResultsRef = getRefPath(getTeamPath(team_code) + '/workaround_results');

		// First, get the current data to determine what already exists
		const workaroundSnapshot = await nativeGet(workaroundResultsRef);
		const existingWorkaroundCount = workaroundSnapshot.exists() ? Object.keys(workaroundSnapshot.val()).length : 0;
		let workaroundLoadedCount = 0;

		nativeOnChildAdded(
			workaroundResultsRef,
			(snapshot) => {
				// Skip existing items during initial load
				workaroundLoadedCount++;
				if (workaroundLoadedCount <= existingWorkaroundCount) {
					return;
				}

				const workaroundResult = {
					question_id: snapshot.key
				};

				bdjs.trigger("bd__workaroundResultAdded", workaroundResult);
			}
		);

		// Listen for new timeout questions
		const timeoutQuestionsRef = getRefPath(getTeamPath(team_code) + '/timeout_questions');

		// First, get the current data to determine what already exists
		const timeoutSnapshot = await nativeGet(timeoutQuestionsRef);
		const existingTimeoutCount = timeoutSnapshot.exists() ? Object.keys(timeoutSnapshot.val()).length : 0;
		let timeoutLoadedCount = 0;

		nativeOnChildAdded(
			timeoutQuestionsRef,
			(snapshot) => {
				// Skip existing items during initial load
				timeoutLoadedCount++;
				if (timeoutLoadedCount <= existingTimeoutCount) {
					return;
				}

				const value = snapshot.val();
				const timeoutResult = {
					question_id: snapshot.key,
					timed_out_at: value.timed_out_at
				};

				bdjs.trigger("bd__questionTimedOut", timeoutResult);
			}
		);
	};

	const addPlayerToTeam = async (team_code, player_id) => {
		const refPlayer = getRefPlayer(team_code, player_id);
		await nativeSet(refPlayer, {
			player_id: player_id,
			joined_at: serverTimestamp(),
		});
	}


	const get = (path) => {
		return nativeGet(getRefPath(path));
	};

	const push = (path, obj) => {
		return nativePush(getRefPath(path), obj);
	};

	const update = (path, obj) => {
		return nativeUpdate(getRefPath(path), obj);
	};

	const set = (path, obj) => {
		return nativeSet(getRefPath(path), obj);
	};

	const remove = (path) => {
		return nativeRemove(getRefPath(path));
	};

	const onValue = (path, callback) => {
		return nativeOnValue(getRefPath(path), callback);
	};

	const offValue = (path, callback) => {
		return nativeOff(getRefPath(path), "value", callback);
	};

	const setIfNotExists = async (path, obj) => {
		const snapshot = await nativeGet(getRefPath(path));
		if (!snapshot.exists()) {
			await nativeSet(getRefPath(path), obj);
			return true; // Value was set
		}
		return false; // Value already exists, not set
	};

	const getServerTimeOffset = (callback) => {
		// Get server time offset from Firebase
		// .info/serverTimeOffset is a special path that provides the offset in milliseconds
		const offsetRef = ref(getDatabase(), '.info/serverTimeOffset');

		return nativeOnValue(offsetRef, (snapshot) => {
			const offset = snapshot.val() || 0;
			if (callback) callback(offset);
		}, { onlyOnce: true });
	};

	const getServerTime = () => {
		// Get server time offset from Firebase
		const offsetRef = ref(getDatabase(), '.info/serverTimeOffset');

		// eslint-disable-next-line no-undef
		return new Promise((resolve) => {
			nativeOnValue(offsetRef, (snapshot) => {
				const offset = snapshot.val() || 0;
				resolve(Date.now() + offset);
			}, { onlyOnce: true });
		});
	};


	return {
		init,
		firebaseListener,
		addPlayerToTeam,
		get,
		push,
		update,
		set,
		remove,
		onValue,
		offValue,
		setIfNotExists,
		getServerTime,
		getServerTimeOffset,
		getRefTeam,
		getRefPlayer,
		getTeamPath,
		getPlayerPath,
		serverTimestamp,
	}
};

export default fb;